import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useConfigurationCanvas } from '../../components/pages/configuration-canvas/public/useConfigurationCanvas';
import { addSfStage } from '../../reducers/united-canvas/unitedCanvasReducer';
import { aStage } from '../../models/stageModel';
import { v4 as uuidv4 } from 'uuid';

export const useAddTreatmentReviewStage = () => {
  const dispatch = useDispatch();
  const { canvasFunnelMap } = useConfigurationCanvas();

  const addTreatmentReviewStage = useCallback(() => {
    console.log('🔧 addTreatmentReviewStage called');
    
    if (!canvasFunnelMap?.funnelsData) {
      console.warn('No funnel map data available');
      return;
    }

    // Find the "Active Patient Care" funnel
    const activePatientCareFunnel = Object.values(canvasFunnelMap.funnelsData).find(
      (funnel) => funnel.name.startsWith('Active Patient Care')
    );

    if (!activePatientCareFunnel) {
      console.warn('Active Patient Care funnel not found');
      return;
    }

    console.log('Found Active Patient Care funnel:', activePatientCareFunnel);

    // Find "In Treatment" and "Completed" stages
    const inTreatmentStage = activePatientCareFunnel.funnelDetails.stages.find(
      (stage) => stage.stageName === 'In Treatment'
    );
    
    const completedStage = activePatientCareFunnel.funnelDetails.stages.find(
      (stage) => stage.stageName === 'Completed'
    );

    if (!inTreatmentStage) {
      console.warn('In Treatment stage not found');
      return;
    }

    if (!completedStage) {
      console.warn('Completed stage not found');
      return;
    }

    console.log('Found In Treatment stage:', inTreatmentStage);
    console.log('Found Completed stage:', completedStage);

    // Find the exit criteria from "In Treatment" that goes to "Completed"
    const inTreatmentToCompletedExitCriteria = inTreatmentStage.exitCriteria.find(
      (criteria) => criteria._nextStageId === completedStage._stageId
    );

    if (!inTreatmentToCompletedExitCriteria) {
      console.warn('No exit criteria found from In Treatment to Completed');
      return;
    }

    console.log('Found In Treatment to Completed exit criteria:', inTreatmentToCompletedExitCriteria);

    // Create the new "Treatment Review" stage
    const newStageId = uuidv4();
    const newStage = aStage({
      _stageId: newStageId,
      stageName: 'Treatment Review',
      stageDescription: 'Review treatment progress and outcomes',
      _branchIndex: inTreatmentStage._branchIndex, // Same branch as In Treatment
      _stageColumnIndex: (inTreatmentStage._stageColumnIndex || 0) + 1, // Between In Treatment and Completed
      exitCriteria: [
        {
          ...inTreatmentToCompletedExitCriteria,
          _exitCriteriaId: uuidv4(),
          _nextStageId: completedStage._stageId, // Points to Completed
        }
      ],
    });

    console.log('Created new Treatment Review stage:', newStage);

    // Prepare the changes for the addSfStage action
    const stagesPositionChanges = [
      // Move Completed and subsequent stages to the right
      {
        stageId: completedStage._stageId,
        newPosition: {
          row: completedStage._branchIndex,
          column: (completedStage._stageColumnIndex || 0) + 1,
        },
      },
    ];

    // Find stages after Completed that need to be moved
    const stagesAfterCompleted = activePatientCareFunnel.funnelDetails.stages.filter(
      (stage) => (stage._stageColumnIndex || 0) > (completedStage._stageColumnIndex || 0)
    );

    stagesAfterCompleted.forEach((stage) => {
      if (stage._stageId !== completedStage._stageId) {
        stagesPositionChanges.push({
          stageId: stage._stageId,
          newPosition: {
            row: stage._branchIndex,
            column: (stage._stageColumnIndex || 0) + 1,
          },
        });
      }
    });

    // Update "In Treatment" exit criteria to point to the new "Treatment Review" stage
    const stagesNextStageChanges = [
      {
        stageId: inTreatmentStage._stageId,
        exitCriteriaId: inTreatmentToCompletedExitCriteria._exitCriteriaId,
        newNextStageId: newStageId,
      },
    ];

    console.log('Dispatching addSfStage with:', {
      funnelId: activePatientCareFunnel.id,
      newStage,
      stagesPositionChanges,
      stagesNextStageChanges,
    });

    // Dispatch the action to add the new stage
    dispatch(addSfStage({
      funnelId: activePatientCareFunnel.id,
      newStage,
      stagesPositionChanges,
      stagesNextStageChanges,
    }));

    console.log('✅ Treatment Review stage added successfully!');
  }, [dispatch, canvasFunnelMap]);

  return { addTreatmentReviewStage };
};
